<template>
  <div class="row">
    <!-- Breadcrumb & Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
        <div>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb bg-transparent p-0 mb-1">
              <li class="breadcrumb-item">
                <router-link :to="{ name: 'discount' }" class="text-muted text-decoration-none">
                  {{ $t('superadmin.discountsTitle') }}
                </router-link>
              </li>
              <li class="breadcrumb-item active text-white" aria-current="page">
                {{ $t('superadmin.discountDetailTitle') }}
              </li>
            </ol>
          </nav>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.discountDetailTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.discountDetailSubtitle') }}</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button type="button" @click="back" class="btn btn-secondary mr-2">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <router-link
            :to="{ name: 'Editdiscont', params: { id: id } }"
            class="btn btn-primary mr-2"
          >
            <i class="mdi mdi-pencil mr-1"></i> {{ $t('superadmin.editDiscountBtn') }}
          </router-link>
          <button
            type="button"
            @click="confirmDelete"
            class="btn btn-danger"
          >
            <i class="mdi mdi-delete-outline mr-1"></i> {{ $t('superadmin.deleteDiscountBtn') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="col-12 col-xl-10 grid-margin">
      <div v-if="loading" class="card">
        <div class="card-body text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('common.loading') }}</span>
          </div>
          <p class="text-muted mt-2">{{ $t('common.loading') }}</p>
        </div>
      </div>

      <div v-else class="card">
        <div class="card-body">
          <!-- Hero Section inside card -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center p-3 mb-4 rounded-lg" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;">
            <div class="d-flex align-items-center mb-3 mb-md-0">
              <div
                class="rounded-lg mr-3 d-flex align-items-center justify-content-center"
                style="width: 52px; height: 52px; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.3); border-radius: 12px; flex-shrink: 0;"
              >
                <i class="mdi mdi-ticket-percent text-warning" style="font-size: 28px;"></i>
              </div>
              <div>
                <h4 class="text-white font-weight-bold mb-1">{{ discount.title || ($currentLocale === 'id' ? 'Voucher Promo' : 'Promo Voucher') }}</h4>
                <div class="d-flex align-items-center gap-2">
                  <span class="text-muted small">{{ $t('superadmin.discountCode') }}:</span>
                  <span class="badge badge-outline-warning font-mono font-weight-bold" style="letter-spacing: 1px; font-size: 14px;">
                    {{ discount.code || '-' }}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <span
                class="badge px-3 py-2"
                :class="isExpired(discount.expired_at) ? 'badge-danger' : 'badge-success'"
                style="font-size: 13px;"
              >
                <i class="mdi mr-1" :class="isExpired(discount.expired_at) ? 'mdi-clock-alert-outline' : 'mdi-check-decagram'"></i>
                {{ isExpired(discount.expired_at) ? $t('superadmin.statusExpired') : $t('superadmin.statusActive') }}
              </span>
            </div>
          </div>

          <!-- Detail Grid -->
          <div class="row g-4">
            <div class="col-md-6">
              <div class="p-3 rounded-lg h-100" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-3">{{ $t('superadmin.discountValue') }}</h6>
                
                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('superadmin.discountType') }}</span>
                  <span class="text-white font-weight-medium text-capitalize">
                    {{ discount.type === 'percent' ? $t('superadmin.percentage') : $t('superadmin.flatAmount') }}
                  </span>
                </div>

                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('superadmin.discountValue') }}</span>
                  <span class="text-warning font-weight-bold" style="font-size: 20px;">
                    {{ discount.type === 'percent' ? `${discount.value}% OFF` : `Rp ${formatPrice(discount.value)} OFF` }}
                  </span>
                </div>

                <div>
                  <span class="text-muted small d-block">{{ $t('superadmin.usageLimit') }}</span>
                  <span class="text-white font-weight-medium">
                    {{ discount.limit_user ? `${discount.limit_user} ${$currentLocale === 'id' ? 'Perusahaan' : 'Tenants'}` : ($currentLocale === 'id' ? 'Tanpa Batas' : 'Unlimited') }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="p-3 rounded-lg h-100" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-3">{{ $t('superadmin.expiryDate') }}</h6>
                
                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $currentLocale === 'id' ? 'Tanggal Dibuat' : 'Created Date' }}</span>
                  <span class="text-white font-weight-medium">
                    {{ formatDate(discount.created_at) }}
                  </span>
                </div>

                <div class="mb-3">
                  <span class="text-muted small d-block">{{ $t('superadmin.expiryDate') }}</span>
                  <span :class="isExpired(discount.expired_at) ? 'text-danger font-weight-bold' : 'text-white font-weight-medium'">
                    {{ formatDate(discount.expired_at) }}
                  </span>
                </div>

                <div>
                  <span class="text-muted small d-block">{{ $currentLocale === 'id' ? 'Terakhir Diperbarui' : 'Last Updated' }}</span>
                  <span class="text-muted small">
                    {{ formatDate(discount.updated_at) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="col-12 mt-3">
              <div class="p-3 rounded-lg" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px;">
                <h6 class="text-muted text-uppercase small font-weight-bold mb-2">{{ $t('common.description') }}</h6>
                <p class="text-white mb-0" style="white-space: pre-line; line-height: 1.6;">
                  {{ discount.desc || ($currentLocale === 'id' ? 'Tidak ada deskripsi tambahan untuk kode promo ini.' : 'No additional description provided for this promo code.') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDetailDiscount, deleteDiscount } from "@/services/discountServices";
import Swal from "sweetalert2";
import moment from "moment";

export default {
  name: "DiscountDetailView",
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      discount: {},
      loading: true,
    };
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const response = await getDetailDiscount(this.id);
        this.discount = response.data || response || {};
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$t('common.error'),
          text: this.$currentLocale === 'id' ? "Data voucher promo tidak ditemukan." : "Promo voucher data not found.",
          confirmButtonColor: "#3b82f6",
        });
        this.back();
      } finally {
        this.loading = false;
      }
    },
    formatPrice(num) {
      if (!num) return "0";
      return Number(num).toLocaleString("id-ID");
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMMM YYYY");
    },
    isExpired(date) {
      if (!date) return false;
      return moment(date).isBefore(moment());
    },
    back() {
      this.$router.push({ name: "discount" });
    },
    confirmDelete() {
      Swal.fire({
        title: this.$t('superadmin.confirmDeleteDiscountTitle') || "Delete Promo Voucher?",
        text: (this.$t('superadmin.confirmDeleteDiscountText') || 'Voucher "{code}" will be permanently removed.').replace('{code}', this.discount.code || ''),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#64748b",
        confirmButtonText: this.$t('common.delete') || "Yes, Delete",
        cancelButtonText: this.$t('common.cancel') || "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteDiscount(this.id);
            Swal.fire({
              icon: "success",
              title: this.$t('common.success') || "Success",
              text: this.$t('superadmin.discountDeletedSuccess') || "Promo voucher has been removed.",
              confirmButtonColor: "#3b82f6",
            });
            this.$router.push({ name: "discount" });
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: this.$t('common.error') || "Error",
              text: this.$currentLocale === 'id' ? "Terjadi kesalahan saat menghapus voucher diskon." : "An error occurred while deleting discount.",
              confirmButtonColor: "#3b82f6",
            });
          }
        }
      });
    },
  },
};
</script>
